import {groupBy, numericCompareDesc, stringCompare} from '@lib/utils';
import {ConnectionUser, getAccessProfile, getAllConnectedUsers, getAllConnectionsCheckInStatus} from '../..';
import {LocalDateString, NodeId, OfficeWorkday, Workday, Workspace} from './../apiTypes';
import createCachedSelector from 're-reselect';
import {getISOWeek} from 'date-fns';
import {reservationCountQueryForWorkday, reservationCountQueryKey, dateOverlapsWithReservation} from '../utils';
import {
  DeskListItem,
  getAcceptedConnectedUsers,
  getAreaById,
  getBuildingById,
  getConnectedUserById,
  getFloorById,
  RootState,
} from '../../..';
import {AreaDto, BuildingDto, FloorDto} from '@lib/infrastructure';
import {dateKeyForDateTimeString} from '../dateTimeHelpers';

export type OfficeWorkdayParams = Pick<OfficeWorkday, 'buildingId' | 'localStart' | 'localEnd'>;
/**
 * Returns scheduled workdays for a date.
 * Returns `null` if no load was ever triggered for the date.
 */
export const getWorkdaysForDate = (dateKey: LocalDateString) => (state: RootState) => {
  const entry = state.data.timeslots.workdaysByDate[dateKey];
  return entry ?? null;
};

export const getWorkspaceReservationsForDate = (dateKey: LocalDateString) => (state: RootState) => {
  const entry = state.data.timeslots.workspaceReservationsByDate[dateKey];
  return entry ?? null;
};

export const getConfirmedWorkspaceReservationsForWorkday = (workdayId: string | null) => (state: RootState) => {
  if (workdayId === null) return [];

  // Note: This can be made more efficient by only querying for the workday date first
  return getAllWorkspaceReservations(state).filter((r) => r.status === 'Confirmed' && r.workdayId === workdayId);
};

export const getWorkdaysWithWorkspaceReservationsForDate = (dateKey: LocalDateString) => (state: RootState) => {
  const workdayData = state.data.timeslots.workdaysByDate[dateKey];
  const workspaceReservationData = state.data.timeslots.workspaceReservationsByDate[dateKey];

  if (!workdayData || !workspaceReservationData) return null;

  const reservationsByWorkdayId = groupBy(workspaceReservationData.data ?? [], (r) => r.workdayId ?? 'unparented');

  return {
    workdays: (workdayData.data ?? []).map((workday) => ({
      workday,
      workspaceReservations: reservationsByWorkdayId[workday.id] ?? [],
    })),
    unparented: reservationsByWorkdayId.unparented ?? [],
  };
};

export const getScheduleForDateIsLoading = (dateKey: LocalDateString) => (state: RootState) => {
  return (
    state.data.timeslots.workdaysByDate[dateKey]?.status === 'pending' ||
    state.data.timeslots.workspaceReservationsByDate[dateKey]?.status === 'pending'
  );
};

export const getScheduleForDateIsUpdating = (dateKey: LocalDateString) => (state: RootState) => {
  return state.data.timeslots.pendingMutations.some((c) => {
    const reservation = c.type === 'patch' ? c.data.original : c.data;
    return dateOverlapsWithReservation(reservation.localStart, reservation.localEnd, dateKey);
  });
};

// Copy of the one used for avatar stacks in week day cells
export const getSortedConnectedUsersWithOfficeDayOnDate = createCachedSelector(
  [
    (state: RootState, dateKey: string) => state.data.timeslots.connectionWorkdaysByDate[dateKey],
    getAllConnectedUsers,
    getAllConnectionsCheckInStatus,
  ],
  (workdayEntry, connectedUsers, checkInStatus): ConnectionUser[] => {
    if (workdayEntry?.status !== 'fulfilled') return [];

    const userIdsWithOfficeDay = new Set<string>();
    for (const workday of workdayEntry.data) {
      if (workday.status === 'OfficeDay') {
        userIdsWithOfficeDay.add(workday.ownerId);
      }
    }

    const userIdsCheckedIn = new Set<string>();
    for (const checkIn of checkInStatus) {
      if (checkIn.eventType === 'AtTheOffice' && checkIn.userId) {
        userIdsCheckedIn.add(checkIn.userId);
      }
    }

    return connectedUsers
      .filter((user) => userIdsWithOfficeDay.has(user.id))
      .sort(
        (a, b) =>
          // Two groups: checked-in to the front, others to the back
          numericCompareDesc(userIdsCheckedIn.has(a.id) ? 1 : 0, userIdsCheckedIn.has(b.id) ? 1 : 0) ||
          // Within groups, sort alphabetically
          stringCompare(a.name, b.name),
      );
  },
)((_state, date) => date);

export const getSortedConnectedUsersWithReservationOnDateInNode = createCachedSelector(
  [
    (state: RootState, {dateKey}: {dateKey: LocalDateString; nodeId: NodeId}) =>
      state.data.timeslots.connectionWorkspaceReservationsByDate[dateKey],
    getAllConnectedUsers,
    (_state, {nodeId}) => nodeId,
  ],

  (reservationsEntry, connectedUsers, nodeId): Array<ConnectionUser> => {
    if (reservationsEntry?.status !== 'fulfilled') return [];

    const userIdsWithReservationInNode = new Set(
      reservationsEntry.data
        .filter((d) => {
          // Direct match
          if (d.workspace.nodeId === nodeId) return true;

          // In building
          if (d.workspace.buildingId === nodeId) return true;
          // On floor
          if ('floorId' in d.workspace && d.workspace.floorId === nodeId) return true;
          // In area
          if ('areaId' in d.workspace && d.workspace.areaId === nodeId) return true;

          // In different tree
          return false;
        })
        .map((r) => r.owner.id),
    );

    return connectedUsers
      .filter((user) => userIdsWithReservationInNode.has(user.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  },
)((_state, {dateKey, nodeId}) => `${dateKey}_${nodeId}`);

export const getQuotaAvailableInWeek = (state: RootState, date: LocalDateString) => {
  const profile = getAccessProfile(state);
  const weekNr = getISOWeek(new Date(date));

  const relevantEntries = Object.entries(state.data.timeslots.workspaceReservationsByDate)
    .filter(([k, v]) => v?.status === 'fulfilled')
    .filter(([k, v]) => {
      return getISOWeek(new Date(k)) === weekNr;
    });

  const daysWithAtLeastOneConfirmedBooking = relevantEntries.filter(([k, v]) => {
    return v?.data?.some((r) => r.status === 'Confirmed');
  });

  return profile.daysPerWeek - daysWithAtLeastOneConfirmedBooking.length;
};

export const getSomeWorkdaysHaveLoaded = (state: RootState) => {
  return Object.values(state.data.timeslots.workdaysByDate).some((e) => e?.status === 'fulfilled');
};

export const getSomeWorkspaceReservationsHaveLoaded = (state: RootState) => {
  return Object.values(state.data.timeslots.workspaceReservationsByDate).some((e) => e?.status === 'fulfilled');
};

export const getAllWorkdays = (state: RootState) => {
  return Object.values(state.data.timeslots.workdaysByDate).flatMap((e) => e?.data ?? []);
};

export const getAllWorkspaceReservations = (state: RootState) => {
  return Object.values(state.data.timeslots.workspaceReservationsByDate).flatMap((e) => e?.data ?? []);
};

export const getWorkdayBuilding = (state: RootState, workday?: Workday | null) =>
  (workday?.status === 'OfficeDay' && state.data.buildings.entities[workday.buildingId]) || null;

// These selectors re-implement getListOfSelectableWorkingSpacesByTypeNew trying to stay as close to the format as possible...

// Replaces getListOfSelectableWorkingSpaces, stripping out connected users
type SelectableWorkspace =
  | {
      type: 'building';
      capacity: number;
      building: BuildingDto;
      floor: null;
      area: null;
    }
  | {
      type: 'floor';
      capacity: number;
      building: BuildingDto;
      floor: FloorDto;
      area: null;
    }
  | {
      type: 'area';
      capacity: number;
      building: BuildingDto;
      floor: FloorDto;
      area: AreaDto;
    };

export const getSelectableWorkspaces = (state: RootState, buildingId: string): Array<SelectableWorkspace> => {
  const building = state.data.buildings.denormalized.find((b) => b.id === buildingId);
  if (!building) return [];

  if (building.floors.length === 0)
    return [
      {
        type: 'building',
        capacity: building.capacity,
        building,
        floor: null,
        area: null,
      },
    ];

  return building.floors
    .filter((f) => f.capacity > 0)
    .flatMap((floor): SelectableWorkspace | Array<SelectableWorkspace> => {
      // Floor workspace
      if (floor.areas.length === 0)
        return {
          type: 'floor',
          capacity: floor.capacity,
          building,
          floor,
          area: null,
        };

      return floor.areas
        .filter((a) => a.capacity > 0)
        .map((area) => ({
          type: 'area',
          capacity: area.capacity,
          building,
          floor,
          area,
        }));
    });
};

export const getListOfSelectableWorkingSpacesByTypeNew_timeslots = (
  state: RootState,
  {
    workday,
    inviteeCount,
  }: {
    workday: OfficeWorkday;
    inviteeCount: number;
  },
): {Available: Array<DeskListItem>; Unavailable: Array<DeskListItem>} => {
  // 1) Collect availability info
  const availabilityKey = reservationCountQueryKey(reservationCountQueryForWorkday(workday));

  const availabilityEntry = state.data.timeslots.workspaceReservationCounts[availabilityKey];
  if (!availabilityEntry) {
    console.warn('Warning: fetch availability before using getListOfSelectableWorkingSpacesByTypeNew_timeslots');
  }

  // 2) Collect connection info
  const date = dateKeyForDateTimeString(workday.localStart);
  const connectionsEntry = state.data.timeslots.connectionWorkspaceReservationsByDate[date];
  if (!connectionsEntry) {
    console.warn('Warning: fetch connection details before using getListOfSelectableWorkingSpacesByTypeNew_timeslots');
  }

  const connectionReservationsByNodeId = groupBy(connectionsEntry?.data ?? [], (r) =>
    'areaId' in r.workspace
      ? r.workspace.areaId
      : 'floorId' in r.workspace
      ? r.workspace.floorId
      : r.workspace.buildingId,
  );

  const workspaces = getSelectableWorkspaces(state, workday.buildingId);
  const available = Array<DeskListItem>();
  const unavailable = Array<DeskListItem>();

  for (const workspace of workspaces) {
    const nodeId = workspace.area?.id ?? workspace.floor?.id ?? workspace.building?.id;

    // Resolve connected users
    const connectionReservations = connectionReservationsByNodeId[nodeId] ?? [];
    const connectionIds = new Set(connectionReservations.map((r) => r.owner.id));
    const connectedUsers = Array.from(connectionIds)
      .map((id) => getConnectedUserById(state, id))
      .filter((user): user is ConnectionUser => user !== undefined);

    // Resolve availability
    const bookedCount = availabilityEntry?.data?.countByNodeId[nodeId]?.booked ?? 0;

    // Construct the weird required viewmodel
    const item: DeskListItem = {
      activityTypes: workspace.area?.activityTypes ?? [],
      deskAmenities: workspace.area?.deskAmenities ?? [],
      connectedUsers,
      floorId: workspace.floor?.id ?? '', // This type initially did not support buildings in the list
      hasBookableDesks: workspace.area?.hasBookableDesks ?? false,
      nodeId,
      capacity: workspace.capacity,

      title: workspace.area?.name ?? workspace.floor?.name ?? workspace.building.name,
      subtitle: workspace.type === 'area' ? workspace.floor.name : '',
      areaId: workspace.area?.id,
      workspaceBookingPolicyType: workspace.area?.workspaceBookingPolicyType,
      nodeAvailability: workspace.capacity - bookedCount >= inviteeCount ? 'Available' : 'Unavailable',
    };

    const group = item.nodeAvailability === 'Available' ? available : unavailable;
    group.push(item);
  }

  return {
    Available: available,
    Unavailable: unavailable,
  };
};

export const getWorkspaceAvailability = (state: RootState, workday: OfficeWorkdayParams | null) => {
  if (!workday) return null;

  const key = reservationCountQueryKey(reservationCountQueryForWorkday(workday));
  const data = state.data.timeslots.workspaceReservationCounts[key];

  return data ?? null;
};

export const getReservationCountForBuildingOnWorkday = (state: RootState, workday: OfficeWorkdayParams | null) => {
  if (!workday) return null;

  const building = getBuildingById(state, workday.buildingId);
  if (!building) return null;

  const nodeAvailability = getWorkspaceAvailability(state, workday);
  if (!nodeAvailability?.data) return null;

  const occupied = Object.values(nodeAvailability.data.countByNodeId)
    .map((av) => av?.booked ?? 0)
    .reduce((a, b) => a + b, 0);

  // Note: I guess technically we'd need to compute the capacity based on building.capacity - closed workspaces here...
  return {
    capacity: building.capacity,
    occupied: occupied,
    available: building.capacity - occupied,
  };
};

export const getWorkspaceCapacityAreaUpwards = (state: RootState, workspace: Workspace) => {
  if ('areaId' in workspace) return getAreaById(state, workspace.areaId)?.capacity;
  if ('floorId' in workspace) return getFloorById(state, workspace.floorId)?.capacity;
  return getBuildingById(state, workspace.buildingId)?.capacity;
};

export const getConnectedUsersWithAndWithoutBookingsV2 = (state: RootState) => {
  const connectedUsers = Object.fromEntries(
    getAcceptedConnectedUsers(state)
      .sort((u1, u2) => (u1.name || u1.email).localeCompare(u2.name || u2.email))
      .map((user) => [user.id, {...user, workdays: Array<Workday>()}]),
  );

  const workdays = Object.values(state.data.timeslots.connectionWorkdaysByDate).flatMap((v) => v?.data ?? []);

  for (const wd of workdays) {
    const target = connectedUsers[wd.ownerId]?.workdays ?? [];
    target.push(wd);
  }

  const allConnections = Object.values(connectedUsers);

  return {
    allConnections,
    connectionsWithWorkdays: allConnections.filter((c) => c.workdays.length > 0),
    connectionsWithoutWorkdays: allConnections.filter((c) => c.workdays.length === 0),
  };
};
